/**
 * In-memory implementation of `ApiClient`.
 *
 * **No backend is running.** This is not a mock library and not a fixture file
 * read by tests only — it is the app's data source in local development, and it
 * is what the live adapter (`./generated-adapter.ts`) replaces when
 * `VITE_DATA_SOURCE=live`. It therefore behaves like a server in the ways that
 * matter to the shell: it filters, sorts, paginates, fails, returns nothing,
 * and takes time.
 *
 * It does *not* enforce any invariant. Masking, held-back matching inputs and
 * reveal-on-accept are storage-layer guarantees (I-3, I-4, ADR 0002); a fake
 * that appeared to enforce them would teach the wrong lesson. What it does is
 * return only the shapes the real contract permits, so a screen built against it
 * cannot come to depend on a field the server will never send.
 *
 * The taxonomy below is migration `0008`'s seed verbatim — same ids, same
 * labels. A fake with its own vocabulary would make the switch to live change
 * every label on screen, and that is the moment nobody can tell a bug from the
 * data.
 */

import type {
  AddFormOptionInput,
  ApprovalState,
  CreateProgramInput,
  ManagedForm,
  StaffNotificationPreferences,
  ManagedQuestionOptions,
  UpdateFormOptionInput,
  ProgramDetail,
  ProgramFormDefinition,
  ProgramFormOption,
  ProgramFormSection,
  ProgramListRow,
  SaveProgramSectionInput,
  SaveProgramSectionReceipt,
  SubmitProgramResult,
  ProgramSeatsInput,
  ProgramSeatsReceipt,
  AccountDecisionInput,
  AccountDecisionResult,
  AccountQueueQuery,
  ApiClient,
  DirectoryFilters,
  OrgImage,
  OrgImageContentType,
  OrgImageUpload,
  OrgAgeRange,
  OrgLocation,
  OrgProgramSummary,
  OrgPublicContact,
  OrgDirectoryView,
  OrgView,
  OwnOrgProfile,
  Page,
  PendingAccount,
  DirectoryQuery,
  DirectoryResult,
  DirectoryResults,
  PendingProfile,
  PendingProgram,
  ProfileDecisionInput,
  ProfileDecisionResult,
  ProfileEditReceipt,
  ProfileQueueQuery,
  ProgramDecisionInput,
  ProgramDecisionResult,
  ProgramQueueQuery,
  SessionContext,
  ProgramHouse,
  ScheduleTrack,
  TaxonomyKind,
  TaxonomyTerm,
  UpdateOrgProfileInput,
  WeeklySchedule
} from './contract'
import {
  ApiError,
  EVENING_FROM,
  ORG_HIGHLIGHTS_MAX,
  ORG_HIGHLIGHT_MAX_LENGTH,
  composeHomePayload,
  openBedsAcross,
  reported,
  unreported
} from './contract'
import { GENERATED_FORMS, GENERATED_OPTION_KINDS } from './fake-form.generated'
import { isEmptyScenario, isErrorScenario, isPartialScenario, latencyMs } from './scenario'

// ── Helpers ────────────────────────────────────────────────────────────────

const daysAgo = (days: number): string => new Date(Date.now() - days * 86_400_000).toISOString()

const wait = (): Promise<void> => new Promise(resolve => setTimeout(resolve, latencyMs()))

/** One place the error scenario is injected, so every screen's error path is live. */
// ── Programs (ADR 0007) ──────────────────────────────────────────────────────

/**
 * What the directory calls a program described by each form.
 *
 * `form_definitions.program_type` on the server. The generated fixture carries
 * what `GET /programs/forms/{source}` returns, which does not include it.
 */
const FAKE_PROGRAM_TYPES: Readonly<Record<string, string>> = {
  residential: 'Residential',
  outpatient: 'Outpatient',
  detox: 'Detox',
  sober_living: 'Sober Living'
}

/**
 * The forms the fake serves, generated from a migrated database.
 *
 * This was 278 lines of hand-written fixture, and every option in it was right
 * while the whole thing was wrong: two Provider Type options where the form has
 * five, a handful of modalities where it has thirty-four, and so on down every
 * multi-select. A fixture thinner than the real data lets a feature pass without
 * being exercised, and this one hid a whole control -- the search box appears
 * once a question offers twelve options, no question here reached twelve, so the
 * one place a developer looks at the wizard was the one place the search was
 * invisible.
 *
 * `db/tools/dump_form_fixture.py` now asks a migrated database the same question
 * the endpoint asks, in the same order, and writes the answer for every form.
 * Regenerate it when a migration changes a form; do not edit it by hand.
 */
const FAKE_FORMS: Readonly<Record<string, ProgramFormDefinition>> = GENERATED_FORMS

function fakeForm (source: string): ProgramFormDefinition | undefined {
  return Object.hasOwn(FAKE_FORMS, source) ? FAKE_FORMS[source] : undefined
}

/**
 * The fake directory's listings are residential programs, filled from that form.
 *
 * Thrown at load rather than tolerated: a fixture regenerated against a database
 * with no residential form is a broken fixture, and every listing would render
 * its answers as raw ids.
 */
const FAKE_FORM: ProgramFormDefinition = (() => {
  const form = fakeForm('residential')
  if (form === undefined) {
    throw new Error('fake-form.generated.ts has no residential form; regenerate it')
  }
  return form
})()

/** One program the fake is holding. */
interface FakeProgram {
  id: string
  name: string
  programType: string
  formSource: string
  approvalState: ApprovalState
  version: number
  sectionsComplete: string[]
  text: Record<string, string>
  selections: Record<string, string[]>
  schedules: Record<string, WeeklySchedule>
  /** Further houses by question key (ADR 0013), each with an id once saved. */
  locations: Record<string, ProgramHouse[]>
  /** Further houses' beds, by house id -- outside review, as on the server. */
  houseSeats: Record<string, { total: number | null, open: number | null }>
  seats: { total: number | null, open: number | null }
}

async function gate (): Promise<void> {
  await wait()
  if (isErrorScenario()) {
    throw new ApiError('The service did not respond.', { status: 503, retryable: true })
  }
}

function paginate<T> (items: readonly T[], query: { page?: number, pageSize?: number }): Page<T> {
  const pageSize = query.pageSize ?? 20 // Twenty, as every paginated item specifies.
  const page = Math.max(1, query.page ?? 1)
  return { items: items.slice((page - 1) * pageSize, page * pageSize), page, pageSize, total: items.length }
}

// ── Seed data ──────────────────────────────────────────────────────────────

/**
 * Migration `0008_seed_criteria_taxonomy.py`, verbatim. If the migration
 * changes, change this with it — `api.spec.ts` pins a sample of ids and labels
 * so a drift fails a test rather than surfacing as a label mismatch in dev.
 */
const TAXONOMY: readonly TaxonomyTerm[] = [
  // ── The kinds the directory filters on ──────────────────────────────────
  //
  // These ids are the ones `LISTINGS` already uses, and that is the point: the directory screen reads its filter options from here, so a
  // taxonomy that did not contain them rendered three empty filter groups that
  // could never match anything in the fixture. The web tests missed it because
  // they pass ids directly; a browser found it in ten seconds.
  { id: 'tax_pt_residential', kind: 'program_type', label: 'Residential' },
  { id: 'tax_pt_outpatient', kind: 'program_type', label: 'Outpatient' },
  { id: 'tax_pt_school', kind: 'program_type', label: 'School' },
  { id: 'tax_pop_adults', kind: 'population', label: 'Adults (18-65)' },
  { id: 'tax_pop_adolescents', kind: 'population', label: 'Adolescents' },
  { id: 'tax_pop_veterans', kind: 'population', label: 'Veterans' },
  // Not diagnoses. The real form's exclusionary question mixes these with
  // conditions, which is why storage needs a kind for them.
  { id: 'tax_ac_mania', kind: 'admission_criterion', label: 'Active Mania' },
  { id: 'tax_ac_sexoffender', kind: 'admission_criterion', label: 'Registered Sex Offender' },
  // Additional needs. A capability the program advertises, which is a different
  // question from the diagnoses it accepts — the labels overlap and the two
  // lists do not contain one another.
  { id: 'tax_spec_trauma', kind: 'specialty', label: 'Trauma & PTSD' },
  { id: 'tax_spec_dual', kind: 'specialty', label: 'Co-occurring Disorders (Dual Diagnosis)' },
  { id: 'tax_spec_autism', kind: 'specialty', label: 'Autism Spectrum Disorder (ASD)' },
  { id: 'tax_spec_eating', kind: 'specialty', label: 'Eating Disorders (Medically Stable)' },

  { id: '10000000-0000-4000-8000-000000000001', kind: 'condition', label: 'Opioid use disorder' },
  { id: '10000000-0000-4000-8000-000000000002', kind: 'condition', label: 'Alcohol use disorder' },
  { id: '10000000-0000-4000-8000-000000000003', kind: 'condition', label: 'PTSD' },
  { id: '10000000-0000-4000-8000-000000000004', kind: 'condition', label: 'Eating disorder' },
  { id: '10000000-0000-4000-8000-000000000005', kind: 'condition', label: 'Stimulant use disorder' },
  { id: '10000000-0000-4000-8000-000000000006', kind: 'condition', label: 'Benzodiazepine use disorder' },
  { id: '10000000-0000-4000-8000-000000000007', kind: 'condition', label: 'Cannabis use disorder' },
  { id: '10000000-0000-4000-8000-000000000008', kind: 'condition', label: 'Polysubstance use' },
  { id: '10000000-0000-4000-8000-000000000009', kind: 'condition', label: 'Major depressive disorder' },
  { id: '10000000-0000-4000-8000-000000000010', kind: 'condition', label: 'Anxiety disorder' },
  { id: '10000000-0000-4000-8000-000000000011', kind: 'condition', label: 'Bipolar disorder' },
  { id: '10000000-0000-4000-8000-000000000012', kind: 'condition', label: 'Schizophrenia and psychotic disorders' },
  { id: '10000000-0000-4000-8000-000000000013', kind: 'condition', label: 'Obsessive-compulsive disorder' },
  { id: '10000000-0000-4000-8000-000000000014', kind: 'condition', label: 'Borderline personality disorder' },
  { id: '10000000-0000-4000-8000-000000000015', kind: 'condition', label: 'ADHD' },
  { id: '10000000-0000-4000-8000-000000000016', kind: 'condition', label: 'Suicidality and self-harm' },
  { id: '10000000-0000-4000-8000-000000000017', kind: 'condition', label: 'Co-occurring mental health and substance use' },
  { id: '10000000-0000-4000-8000-000000000018', kind: 'condition', label: 'Chronic pain' },
  { id: '10000000-0000-4000-8000-000000000019', kind: 'condition', label: 'Gambling disorder' },
  { id: '20000000-0000-4000-8000-000000000001', kind: 'level_of_care', label: 'Residential, high intensity (ASAM 3.5)' },
  { id: '20000000-0000-4000-8000-000000000002', kind: 'level_of_care', label: 'Partial hospitalization (ASAM 2.5)' },
  { id: '20000000-0000-4000-8000-000000000003', kind: 'level_of_care', label: 'Intensive outpatient (ASAM 2.1)' },
  { id: '20000000-0000-4000-8000-000000000004', kind: 'level_of_care', label: 'Early intervention (ASAM 0.5)' },
  { id: '20000000-0000-4000-8000-000000000005', kind: 'level_of_care', label: 'Outpatient (ASAM 1.0)' },
  { id: '20000000-0000-4000-8000-000000000006', kind: 'level_of_care', label: 'Residential, low intensity (ASAM 3.1)' },
  { id: '20000000-0000-4000-8000-000000000007', kind: 'level_of_care', label: 'Residential, population-specific (ASAM 3.3)' },
  { id: '20000000-0000-4000-8000-000000000008', kind: 'level_of_care', label: 'Medically monitored inpatient (ASAM 3.7)' },
  { id: '20000000-0000-4000-8000-000000000009', kind: 'level_of_care', label: 'Medically managed inpatient (ASAM 4.0)' },
  { id: '20000000-0000-4000-8000-000000000010', kind: 'level_of_care', label: 'Ambulatory withdrawal management (ASAM 2-WM)' },
  { id: '20000000-0000-4000-8000-000000000011', kind: 'level_of_care', label: 'Residential withdrawal management (ASAM 3.2-WM)' },
  { id: '20000000-0000-4000-8000-000000000012', kind: 'level_of_care', label: 'Medically monitored withdrawal management (ASAM 3.7-WM)' },
  { id: '30000000-0000-4000-8000-000000000001', kind: 'amenity', label: 'Private room' },
  { id: '30000000-0000-4000-8000-000000000002', kind: 'amenity', label: 'Transport provided' },
  { id: '30000000-0000-4000-8000-000000000003', kind: 'amenity', label: 'Family program' },
  { id: '30000000-0000-4000-8000-000000000004', kind: 'amenity', label: 'Private bathroom' },
  { id: '30000000-0000-4000-8000-000000000005', kind: 'amenity', label: 'Detox on site' },
  { id: '30000000-0000-4000-8000-000000000006', kind: 'amenity', label: 'Telehealth available' },
  { id: '30000000-0000-4000-8000-000000000007', kind: 'amenity', label: 'Wheelchair accessible' },
  { id: '30000000-0000-4000-8000-000000000008', kind: 'amenity', label: 'Fitness facility' },
  { id: '30000000-0000-4000-8000-000000000009', kind: 'amenity', label: 'Vocational and job support' },
  { id: '30000000-0000-4000-8000-000000000010', kind: 'amenity', label: 'Aftercare planning' },
  { id: '30000000-0000-4000-8000-000000000011', kind: 'amenity', label: 'Pet friendly' },
  { id: '30000000-0000-4000-8000-000000000012', kind: 'amenity', label: 'On-site childcare' },
  { id: '40000000-0000-4000-8000-000000000001', kind: 'insurance', label: 'Aetna' },
  { id: '40000000-0000-4000-8000-000000000002', kind: 'insurance', label: 'Cigna' },
  { id: '40000000-0000-4000-8000-000000000003', kind: 'insurance', label: 'UnitedHealthcare' },
  { id: '40000000-0000-4000-8000-000000000004', kind: 'insurance', label: 'Blue Cross Blue Shield' },
  { id: '40000000-0000-4000-8000-000000000005', kind: 'insurance', label: 'Humana' },
  { id: '40000000-0000-4000-8000-000000000006', kind: 'insurance', label: 'Kaiser Permanente' },
  { id: '40000000-0000-4000-8000-000000000007', kind: 'insurance', label: 'Optum' },
  { id: '40000000-0000-4000-8000-000000000008', kind: 'insurance', label: 'Carelon Behavioral Health' },
  { id: '40000000-0000-4000-8000-000000000009', kind: 'insurance', label: 'Magellan Health' },
  { id: '40000000-0000-4000-8000-000000000010', kind: 'insurance', label: 'TRICARE' },
  { id: '40000000-0000-4000-8000-000000000011', kind: 'insurance', label: 'Medicaid' },
  { id: '40000000-0000-4000-8000-000000000012', kind: 'insurance', label: 'Medicare' }
]

/**
 * The company-page facts a listing cannot supply (ADR 0014): the about
 * paragraph, the highlights and the links. Locations are not here — the server
 * derives them from approved programs, and so does the fake, from `LISTINGS`.
 */
interface CompanyFixture {
  orgId: string
  name: string
  description: string | null
  highlights: readonly string[]
  contact: OrgPublicContact | null
  profileAgeDays: number | null
}

/**
 * Companies with a published page.
 *
 * `org_liberty` is the full one, modeled on the Summit View example the company
 * page was designed from: an about paragraph, three highlights, all three links,
 * and programs in four cities across two states, so the derived locations list
 * has something to sort. `org_altavista` is the opposite end: a published page
 * with no approved program behind it, which is the empty-locations case.
 */
const COMPANIES: readonly CompanyFixture[] = [
  {
    orgId: 'org_liberty',
    name: 'Liberty Health',
    description:
      'Liberty Health provides comprehensive residential and outpatient treatment for adolescents ' +
      'and adults struggling with substance use, mental health challenges and co-occurring disorders.',
    highlights: ['24/7 Clinical Support', 'Family Involvement', 'Accredited Program'],
    contact: {
      websiteUrl: 'https://liberty.example',
      facebook: 'https://facebook.com/libertyhealth',
      instagram: 'https://instagram.com/libertyhealth'
    },
    profileAgeDays: 0
  },
  {
    orgId: 'org_cottonwood',
    name: 'Cottonwood Canyon Treatment',
    description: null,
    highlights: [],
    contact: { websiteUrl: 'https://cottonwood.example' },
    // Past the staleness threshold, so the caution-styled stamp is reachable
    // without editing code. DESIGN.md section 11: never hide a stale number.
    profileAgeDays: 9
  },
  {
    orgId: 'org_wasatch',
    name: 'Wasatch Back Recovery',
    description: 'Withdrawal management and residential care.',
    highlights: ['Detox on site'],
    contact: null,
    profileAgeDays: 1
  },
  {
    orgId: 'org_altavista',
    name: 'Alta Vista Recovery',
    description: null,
    highlights: [],
    contact: null,
    profileAgeDays: null
  }
]

/**
 * The server's derivation, done the same way: one entry per city, sorted by
 * state and then city. A place with no city is not a location.
 */
function locationsOf (places: readonly { city: string | null | undefined, region: string | null | undefined }[]): OrgLocation[] {
  const seen = new Map<string, OrgLocation>()
  for (const place of places) {
    const city = place.city?.trim()
    const region = place.region?.trim()
    if (!city || !region) continue
    seen.set(`${region}|${city.toLowerCase()}`, { city, region })
  }
  return [...seen.values()].sort((a, b) => a.region.localeCompare(b.region) || a.city.localeCompare(b.city))
}

/**
 * Labels for the ids `LISTINGS` filters on that the migration's taxonomy does not
 * carry. The directory fixture predates the real taxonomy, and the company-page
 * summary has to name what it rolls up.
 */
const LISTING_TERMS: readonly TaxonomyTerm[] = [
  { id: 'tax_ins_aetna', kind: 'insurance', label: 'Aetna' },
  { id: 'tax_ins_bcbs', kind: 'insurance', label: 'Blue Cross Blue Shield' },
  { id: 'tax_dx_sud', kind: 'condition', label: 'Substance use disorder' },
  { id: 'tax_dx_ptsd', kind: 'condition', label: 'Post-traumatic stress disorder' }
]

/** What one approved program contributes to its company's summary. */
interface SummarySource {
  readonly ageMin: number | null
  readonly ageMax: number | null
  readonly termIds: readonly string[]
}

/**
 * The server's `OrgProgramSummary` derivation, done the same way: one entry per
 * term, ordered by label within a kind; each distinct stated age range, sorted
 * by lower bound then upper (open last); a program stating no ages adds none.
 */
function summaryOf (sources: readonly SummarySource[]): OrgProgramSummary {
  const terms = new Map<string, TaxonomyTerm>()
  for (const id of sources.flatMap(source => source.termIds)) {
    const term = TAXONOMY.find(entry => entry.id === id) ?? LISTING_TERMS.find(entry => entry.id === id)
    if (term) terms.set(term.id, term)
  }
  const of = (kind: TaxonomyKind): TaxonomyTerm[] =>
    [...terms.values()].filter(term => term.kind === kind).sort((a, b) => a.label.localeCompare(b.label))

  const ranges = new Map<string, OrgAgeRange>()
  for (const source of sources) {
    if (source.ageMin === null && source.ageMax === null) continue
    ranges.set(`${source.ageMin}-${source.ageMax}`, { min: source.ageMin, max: source.ageMax })
  }

  return {
    programCount: sources.length,
    levelsOfCare: of('level_of_care'),
    ageRanges: [...ranges.values()].sort((a, b) =>
      (a.min ?? 0) - (b.min ?? 0) || (a.max ?? Number.MAX_SAFE_INTEGER) - (b.max ?? Number.MAX_SAFE_INTEGER)),
    insuranceInNetwork: of('insurance'),
    populations: of('population'),
    diagnoses: of('condition')
  }
}

function listingSummarySource (row: FakeListing): SummarySource {
  return {
    ageMin: row.ageMin,
    ageMax: row.ageMax,
    termIds: [...(row.levelsOfCare ?? []), ...row.inNetwork, ...row.populations, ...row.diagnoses]
  }
}

const PENDING_ACCOUNTS: readonly PendingAccount[] = [
  {
    orgId: 'org_pending_1',
    orgName: 'Silver Fork Behavioral Health',
    status: 'pending',
    submittedAt: daysAgo(1)
  },
  {
    orgId: 'org_pending_2',
    orgName: 'Bear River Counseling',
    status: 'pending',
    submittedAt: daysAgo(4)
  }
]

/**
 * The profile review queue, and the submitted content behind each row.
 *
 * Three rows, chosen so the queue demonstrates the distinction it exists for:
 *
 *   - **`org_pending_1`** is a brand-new provider. `liveVersion` is `null`: it is
 *     not in the directory at all and its whole listing is waiting. It also sits
 *     in the *account* queue, so the two staff queues visibly overlap on a first
 *     signup, which is the only case where they do.
 *   - **`org_cottonwood`** is the case that had no surface before this queue
 *     existed — an organization approved long ago, live in the directory on
 *     version 2, whose version 3 edit is waiting. Nothing in
 *     `listPendingAccounts` will ever mention it, because `orgs.status` stopped
 *     moving the day it was approved.
 *   - **`org_dev_provider`** appears only after the signed-in provider submits an
 *     edit, which is what makes the two roles' screens tell one story in dev.
 *
 * `submitted` is the content a reviewer reads through `getOrg`. Without it the
 * fake would offer a decision on a version it could not render, which is exactly
 * the gap this work closes.
 */
interface PendingProfileState {
  orgId: string
  orgName: string
  orgStatus: PendingProfile['orgStatus']
  version: number
  approvalState: PendingProfile['approvalState']
  liveVersion: number | null
  submittedAt: string
  submitted: {
    description: string | null
    highlights: readonly string[]
    image: OrgImage | null
    contact: OwnOrgProfile['contact']
    pendingFields: readonly string[]
  }
}

/**
 * Listings waiting on a decision, for the third staff queue.
 *
 * Two facilities under one company, which is the case ADR 0007 exists for and
 * the one a single-program fixture would never surface: a reviewer has to be
 * able to approve Liberty House East without touching Liberty House West. One
 * of them is a first listing and the other an edit to a live one, so the
 * `live_version` distinction is visible in the fake as well as on the server.
 */
/**
 * What a listed program carries, before the search narrows it.
 *
 * Criteria are held the way the server holds them — a taxonomy id under a mode —
 * rather than as pre-computed booleans, so the fake can answer the same questions
 * the real filter answers rather than a simplified version of them.
 */
interface FakeListing {
  programId: string
  name: string
  programType: string
  programTypeId: string
  orgId: string
  orgName: string
  city: string | null
  region: string | null
  /** Miles from the fake's single origin, or null when nothing can place it. */
  distanceMiles: number | null
  /** What that distance came from. `DIR-04` lets a city stand in for a ZIP code. */
  distanceBasis: 'postal_code' | 'city' | null
  populations: readonly string[]
  diagnoses: readonly string[]
  inNetwork: readonly string[]
  outOfNetwork: readonly string[]
  excludes: readonly string[]
  specialties: readonly string[]
  /**
   * The published age range, where `null` on one end is an open bound and
   * `null` on both is a question the provider never answered.
   *
   * The distinction is the whole of `excludedWithoutAges`: "12 and up" is an
   * answer and compares, and two nulls is a silence that must not be read as
   * zero or as unbounded.
   */
  ageMin: number | null
  ageMax: number | null
  /**
   * Levels of care the program offers, as taxonomy ids. Optional because only the
   * company-page summary reads it; a listing without it contributes no level.
   */
  levelsOfCare?: readonly string[]
  /**
   * The outpatient form's day-treatment answers (migration `0028`), by level.
   * Absent on a residential listing, which the form never asks about schedules.
   */
  dayTreatment?: readonly FakeDayTreatment[]
  /** Attendance labels, as the outpatient form offers them. */
  attendance?: readonly string[]
}

/** One level of care a listing runs, with its schedule and length. */
interface FakeDayTreatment {
  readonly level: 'PHP' | 'IOP' | 'GOP'
  /** Empty for GOP, which the form asks only for a length. */
  readonly tracks: readonly ScheduleTrack[]
  readonly weeks: number
}

/**
 * The directory as the fake serves it.
 *
 * Chosen so every filter has something to prove against something: two programs
 * under one company, one taking an insurer in-network and another out, one that
 * auto-declines an admission criterion, and one with no postal code at all — the
 * case both real forms permit and the one a radius silently drops.
 */
const LISTINGS: readonly FakeListing[] = [
  {
    programId: 'prg_ridgeline',
    name: 'Ridgeline House',
    programType: 'Residential',
    programTypeId: 'tax_pt_residential',
    orgId: 'org_liberty',
    orgName: 'Liberty Health',
    city: 'Provo',
    region: 'UT',
    distanceMiles: 6,
    distanceBasis: 'postal_code',
    populations: ['tax_pop_adults', 'tax_pop_veterans'],
    // Long on purpose: the company page's aside shows the first few and folds
    // the rest behind "Show all", and the fake should reach that state.
    diagnoses: [
      'tax_dx_sud', 'tax_dx_ptsd',
      '10000000-0000-4000-8000-000000000001', '10000000-0000-4000-8000-000000000002',
      '10000000-0000-4000-8000-000000000005', '10000000-0000-4000-8000-000000000008',
      '10000000-0000-4000-8000-000000000009', '10000000-0000-4000-8000-000000000010',
      '10000000-0000-4000-8000-000000000017'
    ],
    inNetwork: ['tax_ins_aetna', '40000000-0000-4000-8000-000000000011'],
    outOfNetwork: [],
    excludes: ['tax_ac_mania'],
    specialties: ['tax_spec_trauma', 'tax_spec_dual'],
    ageMin: 18,
    ageMax: 65,
    levelsOfCare: ['20000000-0000-4000-8000-000000000001']
  },
  {
    programId: 'prg_liberty_south',
    name: 'Liberty House South',
    programType: 'Residential',
    programTypeId: 'tax_pt_residential',
    orgId: 'org_liberty',
    orgName: 'Liberty Health',
    city: 'Orem',
    region: 'UT',
    distanceMiles: 14,
    distanceBasis: 'postal_code',
    populations: ['tax_pop_adolescents'],
    diagnoses: ['tax_dx_sud'],
    inNetwork: [],
    outOfNetwork: ['tax_ins_aetna'],
    excludes: [],
    specialties: ['tax_spec_trauma'],
    ageMin: 12,
    ageMax: 17,
    levelsOfCare: ['20000000-0000-4000-8000-000000000006']
  },
  {
    programId: 'prg_cottonwood',
    name: 'Cottonwood Canyon Treatment',
    programType: 'Residential',
    programTypeId: 'tax_pt_residential',
    orgId: 'org_cottonwood',
    orgName: 'Cottonwood Canyon Treatment',
    city: 'Salt Lake City',
    region: 'UT',
    distanceMiles: 41,
    distanceBasis: 'postal_code',
    populations: ['tax_pop_adults'],
    diagnoses: ['tax_dx_sud', 'tax_dx_ptsd'],
    inNetwork: ['tax_ins_bcbs'],
    outOfNetwork: [],
    excludes: ['tax_ac_sexoffender'],
    specialties: ['tax_spec_dual'],
    // An open upper bound: "18 and up" is a real answer and is not the same as
    // having answered nothing.
    ageMin: 18,
    ageMax: null
  },
  {
    // No postal code, which both real forms permit. Before `DIR-04` a radius
    // search dropped this one and counted it; now its city places it, coarsely,
    // and the row says so.
    programId: 'prg_silver_fork',
    name: 'Silver Fork Adolescent',
    programType: 'School',
    programTypeId: 'tax_pt_school',
    orgId: 'org_silver_fork',
    orgName: 'Silver Fork Behavioral Health',
    city: 'Sandy',
    region: 'UT',
    distanceMiles: 19,
    distanceBasis: 'city',
    populations: ['tax_pop_adolescents'],
    diagnoses: ['tax_dx_ptsd'],
    inNetwork: ['tax_ins_aetna'],
    outOfNetwork: [],
    excludes: [],
    specialties: ['tax_spec_autism', 'tax_spec_trauma'],
    ageMin: 11,
    ageMax: 17
  },
  {
    // Neither a ZIP code nor a city anything can place — a free-text address
    // field makes this reachable, and it is the only shape a radius still has
    // to drop. It is here so the excluded count keeps a case to prove itself
    // on now that `DIR-04` places everything else.
    programId: 'prg_unplaceable',
    name: 'Wasatch Back Recovery',
    programType: 'Residential',
    programTypeId: 'tax_pt_residential',
    orgId: 'org_wasatch',
    orgName: 'Wasatch Back Recovery',
    city: 'Nowhere Springs',
    region: 'UT',
    distanceMiles: null,
    distanceBasis: null,
    populations: ['tax_pop_adults'],
    diagnoses: ['tax_dx_sud'],
    inNetwork: ['tax_ins_aetna'],
    outOfNetwork: [],
    excludes: [],
    specialties: [],
    // Neither end. The state every program written before migration 0021 is in,
    // and the case `excludedWithoutAges` exists to report rather than guess at.
    ageMin: null,
    ageMax: null
  },
  {
    // The outpatient form's day-treatment answers, so the schedule filters have
    // something to find in fake mode: a daytime PHP and an evening IOP that
    // runs longer. Asking for "evening, six weeks or less" must not find it --
    // the evening level is not the short one.
    programId: 'prg_canyon_outpatient',
    name: 'Canyon Outpatient',
    programType: 'Outpatient',
    programTypeId: 'tax_pt_outpatient',
    orgId: 'org_liberty',
    orgName: 'Liberty Health',
    city: 'Provo',
    region: 'UT',
    distanceMiles: 31,
    distanceBasis: 'postal_code',
    populations: [],
    diagnoses: [],
    inNetwork: ['40000000-0000-4000-8000-000000000011', '40000000-0000-4000-8000-000000000007'],
    outOfNetwork: [],
    excludes: [],
    specialties: [],
    ageMin: 18,
    ageMax: null,
    levelsOfCare: ['20000000-0000-4000-8000-000000000002', '20000000-0000-4000-8000-000000000003'],
    dayTreatment: [
      { level: 'PHP', tracks: [{ days: ['mon', 'tue', 'wed', 'thu', 'fri'], start: '09:00', end: '15:00' }], weeks: 4 },
      { level: 'IOP', tracks: [{ days: ['mon', 'wed', 'thu'], start: '18:00', end: '21:00' }], weeks: 10 }
    ],
    attendance: ['Hybrid (can attend a combo of in-person and virtual)']
  }
]

/** An option's id on the outpatient form, by the label the form shows. */
function outpatientOption (questionKey: string, label: string): string | undefined {
  return fakeForm('outpatient')?.sections
    .flatMap(section => section.questions)
    .find(question => question.key === questionKey)
    ?.options.find(option => option.label === label)?.taxonomyId
}

const PENDING_PROGRAMS: readonly PendingProgram[] = [
  {
    programId: 'prg_liberty_east',
    name: 'Liberty House East',
    programType: 'Residential, high intensity (ASAM 3.5)',
    orgId: 'org_liberty',
    orgName: 'Liberty Health',
    orgStatus: 'approved',
    version: 4,
    approvalState: 'pending',
    liveVersion: 3,
    city: 'Durham',
    region: 'NC',
    submittedAt: daysAgo(3)
  },
  {
    programId: 'prg_liberty_west',
    name: 'Liberty House West',
    programType: 'Sober living',
    orgId: 'org_liberty',
    orgName: 'Liberty Health',
    orgStatus: 'approved',
    version: 1,
    approvalState: 'pending',
    liveVersion: null,
    city: 'Asheville',
    region: 'NC',
    submittedAt: daysAgo(1)
  }
]

const PENDING_PROFILES: readonly PendingProfileState[] = [
  {
    orgId: 'org_pending_1',
    orgName: 'Silver Fork Behavioral Health',
    orgStatus: 'pending',
    version: 1,
    approvalState: 'pending',
    liveVersion: null,
    submittedAt: daysAgo(1),
    submitted: {
      description: 'Intensive outpatient and partial hospitalization for adults, opened 2025.',
      highlights: ['Evening programming', 'Telehealth available'],
      image: null,
      contact: { websiteUrl: 'https://silverfork.example', instagram: 'https://instagram.com/silverfork' },
      // A first submission: everything filled in is awaiting review, which is
      // what the server's LEFT JOIN against a non-existent approved version
      // reduces to.
      pendingFields: ['/description', '/highlights', '/contact']
    }
  },
  {
    orgId: 'org_cottonwood',
    orgName: 'Cottonwood Canyon Treatment',
    orgStatus: 'approved',
    version: 3,
    approvalState: 'pending',
    liveVersion: 2,
    submittedAt: daysAgo(6),
    submitted: {
      description: 'Residential and step-down care. Now offering on-site withdrawal management.',
      highlights: ['On-site withdrawal management', 'Step-down care'],
      image: null,
      contact: { websiteUrl: 'https://cottonwood.example' },
      // Only what moved since version 2. A reviewer reads this and knows the
      // description and the highlights are the edit; the website is unchanged
      // and needs no attention.
      pendingFields: ['/description', '/highlights']
    }
  }
]

// ── Search ─────────────────────────────────────────────────────────────────



// ── Own-org profile state ──────────────────────────────────────────────────

interface OwnOrgState {
  description: string | null
  highlights: string[]
  image: OrgImage | null
  contact: OwnOrgProfile['contact']
  approvalState: OwnOrgProfile['approvalState']
  version: number
  pendingFields: string[]
  updatedAt: string
}

/**
 * The fake's one logo: a flat brand-colored square, as a PNG data URI. See
 * `seedOwnOrg` for why it is a data URI and why a PNG.
 */
const FAKE_LOGO =
  'data:image/png;base64,' +
  'iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAIAAABt+uBvAAAAj0lEQVR42u3QMQ0AAAgDsBnB' +
  'EvqRgwNOriZV0FQPhygQJEiQIEGCBAlCkCBBggQJEiQIQYIECRIkSJAgBAkSJEiQIEGCBCFI' +
  'kCBBggQJEoQgQYIECRIkSBCCBAkSJEiQIEGCECRIkCBBggQJQpAgQYIECRIkCEGCBAkSJEiQ' +
  'IEEIEiRIkCBBggQhSJCgv6AFnr5upB5jtDMAAAAASUVORK5CYII='

function seedOwnOrg (): OwnOrgState {
  return {
    description: 'Residential treatment, 30 to 60 days, trauma-informed.',
    highlights: ['24/7 Clinical Support', 'Family Involvement'],

    // Seeded with a picture, so the default dev scenario shows the hero as a
    // provider who has finished setting up sees it. The empty state is
    // reachable through the `fresh` scenario and through Remove.
    //
    // A data URI rather than a URL: the fake client answers with no network
    // behind it, and a link to a real logo would make the dev experience depend
    // on somebody else's CDN staying up. This one is a flat brand-colored
    // square, which is honest about being a placeholder.
    //
    // A real PNG, not an SVG labeled as one. The product refuses SVG — it is a
    // document that executes script — so a fixture serving one under
    // `image/png` would be the fake disagreeing with the rule it exists to
    // exercise.
    image: {
      url:
        FAKE_LOGO,
      contentType: 'image/png'
    },
    contact: { websiteUrl: 'https://ridgeline.example', facebook: 'https://facebook.com/ridgelinerecovery' },
    approvalState: 'approved',
    version: 2,
    pendingFields: [],
    updatedAt: daysAgo(0)
  }
}

// ── Factory ────────────────────────────────────────────────────────────────

export function createFakeApiClient (context: SessionContext): ApiClient {
  const queue = PENDING_ACCOUNTS.map(entry => ({ ...entry }))
  /** Profile versions awaiting the reduced ORG-07 decision, keyed by org id. */
  const pendingProfiles = new Map<string, PendingProfileState>(
    PENDING_PROFILES.map(entry => [entry.orgId, { ...entry, submitted: { ...entry.submitted } }])
  )
  const pendingPrograms = new Map<string, PendingProgram>(
    PENDING_PROGRAMS.map(entry => [entry.programId, { ...entry }])
  )
  const ownOrg = seedOwnOrg()
  /** Mutations are idempotent by key, per ADR 0004 section 6. */
  const idempotency = new Map<string, unknown>()

  /**
   * Object URLs for images "uploaded" in this session, keyed by media id.
   *
   * The fake has no object store, so the upload step turns the chosen file into
   * a blob URL and this remembers it until the profile edit names the id. That
   * makes the dev experience real — you see the logo you actually picked, not a
   * placeholder — which is the only way the hero's layout gets exercised
   * against images of different shapes.
   */
  const uploaded = new Map<string, string>()

  function once<T> (key: string, produce: () => T): T {
    if (idempotency.has(key)) return idempotency.get(key) as T
    const value = produce()
    idempotency.set(key, value)
    return value
  }

  // ── Staff option edits (ADR 0015) ─────────────────────────────────────────
  //
  // Copied out of the generated form the first time staff touch a question,
  // then served by getProgramForm in place of the generated list. Array order
  // is served order, which is the server's rule once its ORDER BY has run.

  interface FakeManagedOption {
    taxonomyId: string
    label: string
    canonicalLabel: string
    retired: boolean
  }

  const optionEdits = new Map<string, { ordered: boolean, options: FakeManagedOption[] }>()

  // ADR 0017: dev's default, which this stands in for. Staff opt in; nothing
  // emails somebody who never chose. (Production's default is on.)
  let staffPreferences: StaffNotificationPreferences = {
    email: { account: false, profile: false, program: false }
  }

  function selectQuestion (source: string, key: string): ProgramFormDefinition['sections'][number]['questions'][number] {
    const question = fakeForm(source)?.sections
      .flatMap(section => section.questions)
      .find(q => q.key === key && (q.inputType === 'single_select' || q.inputType === 'multi_select'))
    if (question === undefined || question.options.length === 0) {
      throw new ApiError('That question does not exist.', { status: 404, retryable: false })
    }
    return question
  }

  function editedOptions (source: string, key: string): { ordered: boolean, options: FakeManagedOption[] } {
    const id = `${source}|${key}`
    let entry = optionEdits.get(id)
    if (entry === undefined) {
      entry = {
        ordered: false,
        options: selectQuestion(source, key).options.map(option => ({
          taxonomyId: option.taxonomyId,
          label: option.label,
          canonicalLabel: option.label,
          retired: false
        }))
      }
      optionEdits.set(id, entry)
    }
    return entry
  }

  function alphabetize (options: FakeManagedOption[]): void {
    options.sort((a, b) =>
      Number(a.canonicalLabel === 'Other') - Number(b.canonicalLabel === 'Other') ||
      a.label.localeCompare(b.label))
  }

  function kindOf (taxonomyId: string): TaxonomyKind {
    return GENERATED_OPTION_KINDS[taxonomyId] ?? 'condition'
  }

  function managedQuestion (source: string, key: string): ManagedQuestionOptions {
    const question = selectQuestion(source, key)
    const { ordered, options } = editedOptions(source, key)
    const followUps = (fakeForm(source)?.sections ?? [])
      .flatMap(section => section.questions)
      .filter(q => q.revealedBy?.questionKey === key)
      .map(q => q.revealedBy?.taxonomyId)

    return {
      source,
      key,
      question: question.question,
      kinds: [...new Set(question.options.map(o => kindOf(o.taxonomyId)))].sort(),
      ordered,
      alsoAskedOn: Object.keys(FAKE_FORMS)
        .filter(other => other !== source && (fakeForm(other)?.sections ?? [])
          .some(section => section.questions.some(q => q.key === key && q.options.length > 0)))
        .sort(),
      options: options.map(option => ({
        taxonomyId: option.taxonomyId,
        label: option.label,
        canonicalLabel: option.canonicalLabel,
        kind: kindOf(option.taxonomyId),
        retired: option.retired,
        protectedReason: option.canonicalLabel === 'Other'
          ? 'Other opens a free-text question on this form.'
          : followUps.includes(option.taxonomyId)
            ? 'A follow-up question on this form is shown when this is chosen.'
            : null,
        programCount: 0
      }))
    }
  }

  function withEdits (form: ProgramFormDefinition): ProgramFormDefinition {
    return {
      ...form,
      sections: form.sections.map(section => ({
        ...section,
        questions: section.questions.map(question => {
          const edits = optionEdits.get(`${form.source}|${question.key}`)
          if (edits === undefined) return question
          return {
            ...question,
            options: edits.options.map((option): ProgramFormOption => ({
              taxonomyId: option.taxonomyId,
              label: option.label,
              ...(option.retired ? { retired: true as const } : {})
            }))
          }
        })
      }))
    }
  }

  function conflict (detail: string): ApiError {
    return new ApiError(detail, { status: 409, retryable: false })
  }


  function ownProfile (): OwnOrgProfile {
    return {
      view: 'owner',
      orgId: 'org_dev_provider',
      name: 'Ridgeline Recovery Center',
      orgStatus: 'approved',
      approvalState: isPartialScenario() ? 'info_requested' : ownOrg.approvalState,
      version: ownOrg.version,
      pendingFields: isPartialScenario() ? ['/description', '/highlights'] : ownOrg.pendingFields,
      description: ownOrg.description,
      highlights: [...ownOrg.highlights],
      image: ownOrg.image,
      contact: ownOrg.contact,
      locations: ownLocations(),
      programSummary: ownSummary(),
      updatedAt: ownOrg.updatedAt
    }
  }

  /**
   * The signed-in provider's locations: its **approved** programs and every
   * house of each, as the server derives them. A draft program contributes
   * nothing, so a new provider sees the empty state until staff approve one.
   */
  function ownLocations (): OrgLocation[] {
    return locationsOf(
      programs
        .filter(program => program.approvalState === 'approved')
        .flatMap(program => {
          const row = toProgramRow(program)
          return [{ city: row.city, region: row.region }, ...row.houses]
        })
    )
  }

  /**
   * The signed-in provider's summary, from its approved programs' answers: the
   * stated ages and every selected id the taxonomy files under a summarized kind.
   * Out-of-network insurance is asked under its own question and left out.
   */
  function ownSummary (): OrgProgramSummary {
    return summaryOf(
      programs
        .filter(program => program.approvalState === 'approved')
        .map(program => {
          const age = (key: string): number | null => {
            const value = Number.parseInt(program.text[key] ?? '', 10)
            return Number.isNaN(value) ? null : value
          }
          return {
            ageMin: age('age_min'),
            ageMax: age('age_max'),
            termIds: Object.entries(program.selections)
              .filter(([key]) => !key.includes('out_of_network'))
              .flatMap(([, ids]) => ids)
          }
        })
    )
  }

  /**
   * What a staff reviewer reads: the submitted version, in the owner shape —
   * `approvalState` and `pendingFields` are what a reviewer deciding a version
   * needs, and the directory view has neither.
   */
  function reviewView (entry: PendingProfileState): OwnOrgProfile {
    return {
      view: 'owner',
      orgId: entry.orgId,
      name: entry.orgName,
      orgStatus: entry.orgStatus,
      approvalState: entry.approvalState,
      version: entry.version,
      pendingFields: entry.submitted.pendingFields,
      description: entry.submitted.description,
      highlights: [...entry.submitted.highlights],
      image: entry.submitted.image,
      contact: entry.submitted.contact,
      locations: entry.orgId === 'org_dev_provider' ? ownLocations() : companyLocations(entry.orgId),
      programSummary: entry.orgId === 'org_dev_provider' ? ownSummary() : companySummary(entry.orgId),
      updatedAt: entry.submittedAt
    }
  }

  /**
   * The owner shape for an org with nothing awaiting review: its live page,
   * `approved`, with no pending fields.
   */
  function approvedOwnerView (company: OrgDirectoryView): OwnOrgProfile {
    return {
      ...company,
      view: 'owner',
      orgStatus: 'approved',
      approvalState: 'approved',
      version: 1,
      pendingFields: []
    }
  }

  /**
   * An org that exists and has submitted nothing: the empty draft at version 0.
   *
   * Version 0 is a number nothing stores, which is what makes it the honest
   * encoding of "never submitted". A 404 here would read as "no such org" while
   * the org is sitting in the account queue two rows up.
   */
  function emptyOwnerView (row: PendingAccount): OwnOrgProfile {
    return {
      view: 'owner',
      orgId: row.orgId,
      name: row.orgName,
      orgStatus: row.status,
      approvalState: 'draft',
      version: 0,
      pendingFields: [],
      description: null,
      highlights: [],
      image: null,
      contact: null,
      locations: [],
      programSummary: summaryOf([]),
      updatedAt: row.submittedAt
    }
  }

  const programs: FakeProgram[] = []

  function toProgramRow (program: FakeProgram): ProgramListRow {
    return {
      id: program.id,
      name: program.name,
      programType: program.programType,
      formSource: program.formSource,
      approvalState: program.approvalState,
      version: program.version,
      sectionsComplete: [...program.sectionsComplete],
      city: program.text.city ?? null,
      region: program.text.state ?? null,
      seats: { ...program.seats },
      houses: Object.values(program.locations).flat().map(house => ({
        id: house.id ?? '',
        streetAddress: house.streetAddress ?? null,
        city: house.city,
        region: house.region,
        seats: { ...(program.houseSeats[house.id ?? ''] ?? { total: null, open: null }) }
      }))
    }
  }

  /**
   * The published company page.
   *
   * The about paragraph, highlights and links come from `COMPANIES` when the
   * company has an entry there; the locations always come from its listings,
   * because that is where the server gets them. A company that exists only as
   * the owner of listings still answers, with a synthesized description, so a
   * company link from any directory row resolves.
   */
  /**
   * Where a company's approved programs run: its directory listings, plus any
   * program in the review queue that already has an approved version live — an
   * edit waiting does not take the published listing's city off the map.
   */
  function companyLocations (orgId: string): OrgLocation[] {
    return locationsOf([
      ...LISTINGS.filter(row => row.orgId === orgId),
      ...[...pendingPrograms.values()].filter(row => row.orgId === orgId && row.liveVersion !== null)
    ])
  }

  /**
   * What a company's approved programs offer, rolled up from its directory
   * listings — the same rows the company page lists, so the two cannot disagree.
   */
  function companySummary (orgId: string): OrgProgramSummary {
    return summaryOf(LISTINGS.filter(row => row.orgId === orgId).map(listingSummarySource))
  }

  function toCompanyView (orgId: string): OrgDirectoryView | null {
    const fixture = COMPANIES.find(entry => entry.orgId === orgId)
    const owned = LISTINGS.filter(row => row.orgId === orgId)
    if (fixture === undefined && owned.length === 0) return null

    const name = fixture?.name ?? owned[0]!.orgName
    return {
      view: 'directory',
      orgId,
      name,
      description: fixture
        ? fixture.description
        : `${name} runs ${owned.length === 1 ? 'one program' : `${owned.length} programs`} listed in the directory.`,
      highlights: [...(fixture?.highlights ?? [])],
      image: null,
      contact: fixture
        ? fixture.contact
        : { websiteUrl: `https://${orgId.replace(/^org_/, '').replace(/_/g, '-')}.example` },
      locations: companyLocations(orgId),
      programSummary: companySummary(orgId),
      updatedAt: fixture?.profileAgeDays == null ? daysAgo(30) : daysAgo(fixture.profileAgeDays)
    }
  }

  /**
   * A directory listing as `GET /programs/{id}` returns it to a counterparty.
   *
   * `view: 'directory'` with no approval state and no version, which is what the
   * real endpoint omits for anyone but the owner and staff — a listing page that
   * rendered a review state would be showing a reader something the server
   * never sends them.
   *
   * Selections are keyed by the question that asked them, because that is how
   * the answer sheet renders: the form supplies the wording and the labels, and
   * a bag of ids under no question would render as nothing at all.
   */
  function toListingDetail (row: FakeListing): ProgramDetail {
    if (row.dayTreatment !== undefined) return toOutpatientDetail(row)

    // The row's own ids are the fake directory's filter taxonomy, which the
    // generated form does not know — rendered through the form they would
    // arrive as raw ids. So the listing is filled from the form's real options,
    // by label, the way a provider completing the form would fill it. The row
    // still decides the facts that vary between listings.
    const ids = (key: string, labels: readonly string[]): string[] => {
      const question = FAKE_FORM.sections.flatMap(s => s.questions).find(q => q.key === key)
      return labels
        .map(label => question?.options.find(option => option.label === label)?.taxonomyId)
        .filter((id): id is string => id !== undefined)
    }

    const selections: Record<string, readonly string[]> = {
      provider_type: [row.programTypeId],
      modalities: ids('modalities', [
        '12-Step', 'Acceptance & Commitment Therapy (ACT)', 'Adventure Therapy', 'Art Therapy',
        'Cognitive Behavioral Therapy (CBT)', 'Dialectical Behavior Therapy (DBT)', 'EMDR',
        'Equine Therapy', 'Family Therapy', 'Group Therapy', 'Individual Therapy', 'Mindfulness',
        'Motivational Interviewing', 'Trauma-Focused CBT'
      ]),
      specialties: ids('specialties', [
        'Anxiety & Panic Disorders', 'Co-occurring Disorders (Dual Diagnosis)', 'Depression',
        'Substance Use Disorders', 'Trauma & PTSD'
      ]),
      populations: ids('populations', ['Adults (18-65)', 'Veterans', 'LGBTQ+']),
      diagnoses_accepted: ids('diagnoses_accepted', [
        'ADD/ADHD', 'Anxiety & Panic Disorders', 'Bipolar Disorder', 'Depression', 'PTSD',
        'Substance Use Disorder'
      ]),
      exclusionary_criteria: ids('exclusionary_criteria', ['Active Eating Disorder', 'Active Mania', 'Active Psychosis']),
      general_medical: ids('general_medical', ['24/7 On-Site Nursing']),
      insurance_in_network: ids('insurance_in_network', ['Aetna', 'Blue Cross Blue Shield', 'Cigna', 'UnitedHealthcare']),
      insurance_out_of_network: ids('insurance_out_of_network', ['Aetna', 'BCBS', 'Cigna']),
      payment_options: ids('payment_options', ['Private Insurance', 'Out-of-Network Insurance', 'Payment Plans']),
      admission_days: ids('admission_days', ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']),
      intake_hours: ids('intake_hours', ['Daytime Hours (8am-5pm)']),
      languages: ids('languages', ['English', 'Spanish']),
      accessibility: ids('accessibility', ['Wheelchair Accessible'])
    }

    return {
      view: 'directory',
      id: row.programId,
      name: row.name,
      programType: row.programType,
      formSource: 'residential',
      orgId: row.orgId,
      location: {
        streetAddress: row.city !== null ? '1450 N University Ave' : null,
        city: row.city,
        region: row.region,
        postalCode: row.city !== null ? '84604' : null,
        countyRegion: null
      },
      answers: {
        text: {
          program_name: row.name,
          ...(row.city !== null ? { city: row.city } : {}),
          ...(row.region !== null ? { state: row.region } : {}),
          ...(row.ageMin !== null ? { age_min: String(row.ageMin) } : {}),
          ...(row.ageMax !== null ? { age_max: String(row.ageMax) } : {}),
          admissions_phone: '+1 801 555 0100',
          contact_name: 'Jordan Ellis',
          contact_title: 'Admissions Director',
          contact_phone: '+1 801 555 0142',
          contact_email: 'admissions@example.test',
          capacity: '24',
          self_pay_daily_rate: '850',
          hours_of_operation: 'Staffed 24 hours, 7 days a week',
          additional_details:
            `${row.name} is a residential program set on eleven acres at the mouth of the canyon. ` +
            'Clients live in small cottages of six, share meals with staff, and spend afternoons ' +
            'outdoors: hiking, climbing and equine work are part of the weekly schedule rather than ' +
            'electives.\n\nClinical care is led by a psychiatrist on site five days a week and ' +
            'licensed therapists who carry no more than eight clients each. Every client has two ' +
            'individual sessions and a family session each week, and discharge planning begins at ' +
            'intake so the step down to outpatient care is arranged before it is needed.\n\nWe ' +
            'work closely with referring clinicians and send a written update at admission, at the ' +
            'two-week mark and at discharge.'
        },
        selections,
        schedules: {},
        locations: {}
      },
      approvalState: null,
      version: null,
      sectionsComplete: null
    }
  }

  /** An outpatient listing, answered through the outpatient form. */
  function toOutpatientDetail (row: FakeListing): ProgramDetail {
    const ids = (key: string, labels: readonly string[]): string[] =>
      labels.map(label => outpatientOption(key, label)).filter((id): id is string => id !== undefined)

    const levels = row.dayTreatment ?? []
    const text: Record<string, string> = {
      program_name: row.name,
      ...(row.city !== null ? { city: row.city } : {}),
      ...(row.region !== null ? { state: row.region } : {}),
      ...(row.ageMin !== null ? { age_min: String(row.ageMin) } : {}),
      admissions_phone: '+1 801 555 0100'
    }
    const schedules: Record<string, WeeklySchedule> = {}

    for (const level of levels) {
      const prefix = level.level.toLowerCase()
      text[`${prefix}_length_weeks`] = String(level.weeks)
      if (level.tracks.length > 0) {
        schedules[`${prefix}_schedule`] = {
          tracks: level.tracks,
          ...(level.tracks.length > 1 ? { tracksCombinable: false } : {})
        }
      }
    }

    return {
      view: 'directory',
      id: row.programId,
      name: row.name,
      programType: row.programType,
      formSource: 'outpatient',
      orgId: row.orgId,
      location: {
        streetAddress: null,
        city: row.city,
        region: row.region,
        postalCode: null,
        countyRegion: null
      },
      answers: {
        text,
        selections: {
          provider_type: ids('provider_type', ['Outpatient']),
          levels_of_care: ids('levels_of_care', levels.map(level => level.level)),
          iop_attendance: ids('iop_attendance', row.attendance ?? [])
        },
        schedules,
        locations: {}
      },
      approvalState: null,
      version: null,
      sectionsComplete: null
    }
  }

  function requireProgram (id: string): FakeProgram {
    const program = programs.find(p => p.id === id)
    if (program === undefined) {
      throw new ApiError('No such program.', { status: 404, retryable: false })
    }
    return program
  }

  /** The form a program was created from, which decides its sections. */
  function requireForm (program: FakeProgram): ProgramFormDefinition {
    const form = fakeForm(program.formSource)
    if (form === undefined) {
      throw new ApiError('No form is defined for that program.', { status: 500, retryable: false })
    }
    return form
  }

  /**
   * Required questions in one section with no answer yet.
   *
   * A question another answer reveals is required only once that answer is
   * given, as on the server: a PHP schedule is not missing from a program that
   * does not run PHP.
   */
  function missingIn (program: FakeProgram, section: ProgramFormSection): string[] {
    return section.questions
      .filter(question => question.required)
      .filter(question => question.revealedBy === undefined ||
        (program.selections[question.revealedBy.questionKey] ?? []).includes(question.revealedBy.taxonomyId))
      .filter(question =>
        question.inputType === 'weekly_schedule'
          ? program.schedules[question.key] === undefined
          : question.options.length > 0
            ? (program.selections[question.key]?.length ?? 0) === 0
            : (program.text[question.key] ?? '').trim().length === 0)
      .map(question => question.key)
  }

  return {
    getHome: async () => {
      await gate()
      const role = context.currentRole() ?? 'provider_admin'
      return composeHomePayload(role, context.currentDisplayName(), {
        programCount: reported(programs.length),
        // Summed over the programs, as the real adapter does, and a marker rather
        // than a zero when none has reported (I-6).
        bedsOpen: isPartialScenario() ? unreported<number>() : openBedsAcross(programs.map(toProgramRow)),
        pendingAccounts: reported(queue.filter(r => r.status === 'pending').length),
        pendingProfiles: reported(
          [...pendingProfiles.values()].filter(p => p.approvalState === 'pending').length
        ),
        // DESIGN.md section 10: one panel fails, the others still render.
        failedPanelTitles: isPartialScenario() ? ['Recent activity'] : []
      })
    },

    getTaxonomy: async () => {
      await gate()
      return TAXONOMY
    },

    getDirectoryFilters: async (): Promise<DirectoryFilters> => {
      await gate()

      // The fake's taxonomy is small enough that every term in it is one the
      // fixture's programs actually carry, so the narrowing the real endpoint
      // does has nothing to remove here. Said out loud because a reader
      // comparing this with the server would otherwise expect a filter.
      const of = (kind: TaxonomyTerm['kind']) => TAXONOMY.filter(term => term.kind === kind)

      // From the outpatient form rather than the fake's taxonomy, so the ids
      // are the ones a listing's answers carry. Sober Living is on that list
      // as a program type, not a level, and the server drops it by kind.
      const levelsOfCare = (fakeForm('outpatient')?.sections
        .flatMap(section => section.questions)
        .find(question => question.key === 'levels_of_care')?.options ?? [])
        .filter(option => !option.label.startsWith('Sober Living'))
        .map(option => ({ id: option.taxonomyId, kind: 'level_of_care' as const, label: option.label }))
      const attendance = (fakeForm('outpatient')?.sections
        .flatMap(section => section.questions)
        .find(question => question.key === 'iop_attendance')?.options ?? [])
        .map(option => ({ id: option.taxonomyId, kind: 'attendance' as const, label: option.label.replace(/ \(.*\)$/, '') }))

      // Shaped like the real forms: residential has specialties, outpatient has
      // the schedules, and School stands in for sober living -- a type whose
      // form asks neither insurance nor diagnoses.
      const none: readonly TaxonomyTerm[] = []
      const byProgramType = of('program_type').map(programType => {
        const residential = programType.id === 'tax_pt_residential'
        const outpatient = programType.id === 'tax_pt_outpatient'
        const treats = residential || outpatient

        return {
          programType,
          diagnoses: treats ? of('condition') : none,
          insurance: treats ? of('insurance') : none,
          specialties: residential ? of('specialty') : none,
          levelsOfCare: outpatient ? levelsOfCare : none,
          attendance: outpatient ? attendance : none,
          ages: true,
          sessionTimes: outpatient,
          lengthWeeks: outpatient
        }
      })

      return {
        programTypes: of('program_type'),
        diagnoses: of('condition'),
        insurance: of('insurance'),
        specialties: of('specialty'),
        levelsOfCare,
        attendance,
        byProgramType
      }
    },

    getOrgProfile: async (): Promise<OwnOrgProfile> => {
      await gate()
      return ownProfile()
    },

    getOrg: async (id: string): Promise<OrgView> => {
      await gate()
      if (id === 'org_dev_provider' && context.currentOrgId() === 'org_dev_provider') {
        return ownProfile()
      }
      if (id === 'org_dev_provider') {
        // Staff read the latest version; anyone else reads the page as published.
        // The fake keeps one copy, so a reader outside the org sees the provider's
        // latest edit — a simplification the dev role switch never exposes.
        const own = ownProfile()
        if (context.currentRole() === 'mp_admin') return own
        return {
          view: 'directory',
          orgId: own.orgId,
          name: own.name,
          description: own.description,
          highlights: own.highlights,
          image: own.image,
          contact: own.contact,
          locations: own.locations,
          programSummary: own.programSummary,
          updatedAt: own.updatedAt
        }
      }

      // Staff read the owner shape for any org — the review surface for a
      // pending version. `view` names the document, not the reader, and the
      // server does this with the mp_staff role rather than a widened policy.
      if (context.currentRole() === 'mp_admin') {
        const submitted = pendingProfiles.get(id)
        if (submitted) return reviewView(submitted)

        const live = toCompanyView(id)
        if (live) return approvedOwnerView(live)

        const account = queue.find(row => row.orgId === id)
        if (account) return emptyOwnerView(account)

        throw new ApiError('That organization does not exist.', { status: 404, retryable: false })
      }

      // Every company link from a directory row has to resolve: without this,
      // a company that exists only as the owner of listings answered 404, which
      // was found the way the program click-through gap was — by clicking it.
      const company = toCompanyView(id)
      if (company) return company

      throw new ApiError('That organization does not exist.', { status: 404, retryable: false })
    },

    // ── Programs ───────────────────────────────────────────────────────────

    getProgramForm: async (source: string): Promise<ProgramFormDefinition> => {
      await gate()
      const form = fakeForm(source)
      if (form === undefined) {
        throw new ApiError(`No form is defined for ${source}.`, { status: 404, retryable: false })
      }
      return withEdits(form)
    },

    getStaffNotificationPreferences: async (): Promise<StaffNotificationPreferences> => {
      await gate()
      return { email: { ...staffPreferences.email } }
    },

    putStaffNotificationPreferences: async (
      preferences: StaffNotificationPreferences
    ): Promise<StaffNotificationPreferences> => {
      await gate()
      staffPreferences = { email: { ...preferences.email } }
      return { email: { ...staffPreferences.email } }
    },

    listManagedForms: async (): Promise<readonly ManagedForm[]> => {
      await gate()
      return Object.values(FAKE_FORMS)
        .map(form => ({
          source: form.source,
          title: form.title,
          questions: form.sections.flatMap(section => section.questions
            .filter(q => (q.inputType === 'single_select' || q.inputType === 'multi_select') && q.options.length > 0)
            .map(q => {
              const options = optionEdits.get(`${form.source}|${q.key}`)?.options
              return {
                key: q.key,
                question: q.question,
                section: section.title,
                inputType: q.inputType as 'single_select' | 'multi_select',
                optionCount: options?.length ?? q.options.length,
                retiredCount: options?.filter(o => o.retired).length ?? 0
              }
            }))
        }))
        .sort((a, b) => a.title.localeCompare(b.title))
    },

    getManagedQuestionOptions: async (source: string, key: string): Promise<ManagedQuestionOptions> => {
      await gate()
      return managedQuestion(source, key)
    },

    addFormOption: async (source: string, key: string, input: AddFormOptionInput, idempotencyKey: string): Promise<ManagedQuestionOptions> => {
      await gate()
      return once(idempotencyKey, () => {
        const current = managedQuestion(source, key)
        const label = input.label.trim()
        const kind = input.kind ?? (current.kinds.length === 1 ? current.kinds[0] : undefined)
        if (kind === undefined || !current.kinds.includes(kind)) {
          throw new ApiError(`This question's choices are ${current.kinds.join(' or ')}; name one.`, { status: 400, retryable: false })
        }

        // An existing row of that kind and label is reused, as on the server.
        const existing = Object.entries(GENERATED_OPTION_KINDS).find(([id, k]) =>
          k === kind && Object.values(FAKE_FORMS).some(form => form.sections.some(section =>
            section.questions.some(q => q.options.some(o => o.taxonomyId === id && o.label === label)))))
        const taxonomyId = existing?.[0] ?? `tax_${kind}_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`

        for (const target of [source, ...(input.alsoSources ?? [])]) {
          const { ordered, options } = editedOptions(target, key)
          const held = options.find(o => o.taxonomyId === taxonomyId)
          if (held !== undefined) {
            if (held.retired) held.retired = false
            else if (target === source) throw conflict('That choice is already offered on this question.')
            continue
          }
          if (options.some(o => o.label === label)) {
            throw conflict(`"${label}" is already a different choice on the ${target} form.`)
          }
          options.push({ taxonomyId, label, canonicalLabel: label, retired: false })
          if (!ordered) alphabetize(options)
        }
        return managedQuestion(source, key)
      })
    },

    updateFormOption: async (
      source: string,
      key: string,
      taxonomyId: string,
      input: UpdateFormOptionInput,
      idempotencyKey: string
    ): Promise<ManagedQuestionOptions> => {
      await gate()
      return once(idempotencyKey, () => {
        const before = managedQuestion(source, key)
        const shown = before.options.find(o => o.taxonomyId === taxonomyId)
        const option = editedOptions(source, key).options.find(o => o.taxonomyId === taxonomyId)
        if (shown === undefined || option === undefined) {
          throw new ApiError('That choice is not on this question.', { status: 404, retryable: false })
        }
        if (input.retired === true && !option.retired) {
          if (shown.protectedReason !== null) throw conflict(`"${option.label}" cannot be retired. ${shown.protectedReason}`)
          if (before.options.filter(o => !o.retired).length === 1) {
            throw conflict('This is the last choice still offered on this question.')
          }
        }
        if (input.retired !== undefined) option.retired = input.retired
        if (input.label !== undefined) {
          const label = input.label.trim()
          if (before.options.some(o => o.label === label && o.taxonomyId !== taxonomyId)) {
            throw conflict(`"${label}" is already a different choice on this question.`)
          }
          option.label = label
        }
        return managedQuestion(source, key)
      })
    },

    reorderFormOptions: async (
      source: string,
      key: string,
      taxonomyIds: readonly string[] | null,
      idempotencyKey: string
    ): Promise<ManagedQuestionOptions> => {
      await gate()
      return once(idempotencyKey, () => {
        const entry = editedOptions(source, key)
        if (taxonomyIds === null) {
          entry.ordered = false
          alphabetize(entry.options)
        } else {
          const ids = new Set(taxonomyIds)
          if (ids.size !== taxonomyIds.length || ids.size !== entry.options.length ||
              entry.options.some(o => !ids.has(o.taxonomyId))) {
            throw new ApiError('List every choice on the question exactly once.', { status: 400, retryable: false })
          }
          entry.ordered = true
          entry.options.sort((a, b) => taxonomyIds.indexOf(a.taxonomyId) - taxonomyIds.indexOf(b.taxonomyId))
        }
        return managedQuestion(source, key)
      })
    },

    listPrograms: async (): Promise<readonly ProgramListRow[]> => {
      await gate()
      return programs.map(toProgramRow)
    },

    createProgram: async (input: CreateProgramInput, _idempotencyKey: string): Promise<ProgramListRow> => {
      await gate()

      // The form decides the type, which is the whole point of the change that
      // removed `providerType` from this call: a form is one kind of program,
      // and the form's own Provider Type question is a different, narrower
      // thing answered inside it.
      const programType = FAKE_PROGRAM_TYPES[input.formSource]
      if (fakeForm(input.formSource) === undefined || programType === undefined) {
        throw new ApiError('No form is defined for that source.', {
          status: 400,
          retryable: false,
          fields: { form_source: 'Choose one of the kinds of program offered.' }
        })
      }

      const program: FakeProgram = {
        id: `prg_${programs.length + 1}`,
        name: input.name,
        programType,
        formSource: input.formSource,
        approvalState: 'draft',
        version: 1,
        sectionsComplete: [],
        text: { program_name: input.name },
        selections: {},
        schedules: {},
        locations: {},
        houseSeats: {},
        seats: { total: null, open: null }
      }

      programs.push(program)
      return toProgramRow(program)
    },

    getProgram: async (id: string, view?: 'owner' | 'directory'): Promise<ProgramDetail> => {
      await gate()

      // A directory row first, because that is the one path a clinician takes:
      // open a listing from the directory. Without this the fake resolved only
      // the signed-in provider's *own* programs, so every click-through from
      // the directory answered 404 — which the web suite never saw, because it
      // reads the directory and the program through separate calls and never
      // walks from one to the other. A browser found it on the first click.
      const listed = LISTINGS.find(row => row.programId === id)
      if (listed) return toListingDetail(listed)

      const program = requireProgram(id)

      // The owner asking what the directory shows. The fake keeps one version
      // per program, so an unapproved one has nothing listed — the server's 404.
      if (view === 'directory' && program.approvalState !== 'approved') {
        throw new ApiError('This program is not listed yet.', { status: 404, retryable: false })
      }
      const asDirectory = view === 'directory'

      return {
        view: asDirectory ? 'directory' : 'owner',
        id: program.id,
        name: program.name,
        programType: program.programType,
        formSource: program.formSource,
        orgId: 'org_dev_provider',
        location: {
          streetAddress: program.text.street_address ?? null,
          city: program.text.city ?? null,
          region: program.text.state ?? null,
          postalCode: program.text.postal_code ?? null,
          countyRegion: program.text.county_region ?? null
        },
        answers: {
          text: { ...program.text },
          selections: { ...program.selections },
          schedules: { ...program.schedules },
          locations: { ...program.locations }
        },
        approvalState: asDirectory ? null : program.approvalState,
        version: asDirectory ? null : program.version,
        sectionsComplete: asDirectory ? null : [...program.sectionsComplete]
      }
    },

    saveProgramSection: async (
      id: string,
      input: SaveProgramSectionInput,
      _idempotencyKey: string
    ): Promise<SaveProgramSectionReceipt> => {
      await gate()
      const program = requireProgram(id)
      const section = requireForm(program).sections.find(s => s.key === input.section)

      if (section === undefined) {
        throw new ApiError('That form has no such section.', { status: 400, retryable: false })
      }

      for (const [key, value] of Object.entries(input.text ?? {})) {
        program.text[key] = value
      }
      for (const [key, ids] of Object.entries(input.selections ?? {})) {
        program.selections[key] = [...ids]
      }
      // The whole list each time, as on the server: a house keeps the id it came
      // with, and a new one is given one, so its beds stay attached to it.
      for (const [key, houses] of Object.entries(input.locations ?? {})) {
        program.locations[key] = houses.map(house => ({ ...house, id: house.id ?? `house_${Math.random().toString(36).slice(2, 10)}` }))
      }
      // A schedule with no tracks clears the question, as on the server.
      for (const [key, schedule] of Object.entries(input.schedules ?? {})) {
        if (schedule.tracks.length === 0) {
          program.schedules = Object.fromEntries(
            Object.entries(program.schedules).filter(([other]) => other !== key))
        } else {
          program.schedules[key] = schedule
        }
      }

      if (!program.sectionsComplete.includes(section.key)) {
        program.sectionsComplete.push(section.key)
      }

      // Saving never moves the approval state. That is the behavior the whole
      // nine-step design rests on, so the fake has to get it right or a screen
      // would look correct here and be wrong against the server.
      return {
        approvalState: program.approvalState,
        version: program.version,
        section: section.key,
        sectionsComplete: [...program.sectionsComplete],
        missingRequired: missingIn(program, section)
      }
    },

    submitProgram: async (id: string, _idempotencyKey: string): Promise<SubmitProgramResult> => {
      await gate()
      const program = requireProgram(id)

      const missing = requireForm(program).sections.flatMap(section =>
        missingIn(program, section).map(key => `${section.key}/${key}`))

      // A success with a list, not an error. The request was not malformed.
      if (missing.length === 0) {
        program.approvalState = 'pending'

        // And it shows up in the staff queue, which is what makes the loop
        // demonstrable end to end against the fake: build a listing here,
        // switch to the staff role, approve it.
        const existing = pendingPrograms.get(program.id)
        pendingPrograms.set(program.id, {
          programId: program.id,
          name: program.name,
          programType: program.programType,
          orgId: 'org_dev_provider',
          orgName: 'Dev Provider Health',
          orgStatus: 'approved',
          version: program.version,
          approvalState: 'pending',
          liveVersion: existing?.liveVersion ?? null,
          city: program.text.city ?? null,
          region: program.text.state ?? null,
          submittedAt: existing?.submittedAt ?? new Date().toISOString()
        })
      }

      return {
        approvalState: program.approvalState,
        version: program.version,
        missingRequired: missing
      }
    },

    updateProgramSeats: async (
      id: string,
      input: ProgramSeatsInput,
      _idempotencyKey: string
    ): Promise<ProgramSeatsReceipt> => {
      await gate()
      const program = requireProgram(id)

      if (input.total === undefined && input.open === undefined) {
        throw new ApiError('The request changed nothing.', { status: 400, retryable: false })
      }

      // A further house (ADR 0013), which has to be one the program lists.
      const houseId = input.locationId
      if (houseId !== undefined && !Object.values(program.locations).flat().some(house => house.id === houseId)) {
        throw new ApiError('The bed counts were not saved.', {
          status: 400,
          retryable: false,
          fields: { location_id: 'This program lists no such house.' }
        })
      }
      const current = houseId === undefined
        ? program.seats
        : program.houseSeats[houseId] ?? { total: null, open: null }

      // Merged before it is checked, as the server does: sending only `open`
      // still has to sit under the total already recorded.
      const total = input.total ?? current.total
      const open = input.open ?? current.open

      if ((total !== null && total < 0) || (open !== null && open < 0)) {
        throw new ApiError('The bed counts were not saved.', {
          status: 400,
          retryable: false,
          fields: { [total !== null && total < 0 ? 'total' : 'open']: 'Not negative.' }
        })
      }
      if (total !== null && open !== null && open > total) {
        throw new ApiError('The bed counts were not saved.', {
          status: 400,
          retryable: false,
          fields: { open: 'More open beds than total.' }
        })
      }

      if (houseId === undefined) {
        program.seats = { total, open }
      } else {
        program.houseSeats[houseId] = { total, open }
      }

      // No approval state moves and no version opens. The fake has to get this
      // right for the same reason it gets saving right: a screen that looked
      // correct here and sent beds to review against the server would be PRG-11
      // undone.
      return {
        seats: { total, open },
        approvalState: program.approvalState,
        version: program.version
      }
    },

    createOrgImageUpload: async (
      contentType: OrgImageContentType,
      idempotencyKey: string
    ): Promise<OrgImageUpload> => {
      await gate()
      return once(idempotencyKey, (): OrgImageUpload => ({
        mediaId: `media_${Math.random().toString(36).slice(2, 10)}`,
        // Not a real URL, and deliberately unusable. `uploadOrgImage` below
        // never dials it; a fake that returned something plausible would let a
        // test pass while the real adapter's post was broken.
        uploadUrl: 'fake://upload',
        // The shape the real grant has, without a policy only S3 could check.
        fields: { key: 'fake/key', 'Content-Type': contentType },
        maxBytes: 5 * 1024 * 1024,
        contentType,
        expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString()
      }))
    },

    uploadOrgImage: async (upload: OrgImageUpload, file: Blob): Promise<void> => {
      await gate()

      // The one rule the fake can enforce, because it is the one the client is
      // told: everything else in the grant is a policy only S3 evaluates.
      if (file.size > upload.maxBytes) {
        throw new Error('That image is too large.')
      }

      // `createObjectURL` is absent in jsdom, where these tests run. The
      // fallback keeps the id mapped to *something*, so a test can still assert
      // that an image was set without depending on a browser API the test
      // environment does not have.
      uploaded.set(
        upload.mediaId,
        typeof URL.createObjectURL === 'function'
          ? URL.createObjectURL(file)
          : `fake://uploaded/${upload.mediaId}`
      )
    },

    updateOrgProfile: async (input: UpdateOrgProfileInput, idempotencyKey: string): Promise<ProfileEditReceipt> => {
      await gate()

      // The contract's limits, backstopped here as the server backstops them
      // with a 400. The editor communicates them before submit.
      if (input.highlights !== undefined) {
        if (input.highlights.length > ORG_HIGHLIGHTS_MAX) {
          throw new ApiError(`A company page carries at most ${ORG_HIGHLIGHTS_MAX} highlights.`, {
            status: 400,
            retryable: false,
            fields: { highlights: `Keep it to ${ORG_HIGHLIGHTS_MAX} or fewer.` }
          })
        }
        if (input.highlights.some(line => line.length < 1 || line.length > ORG_HIGHLIGHT_MAX_LENGTH)) {
          throw new ApiError('Each highlight must be 1 to 80 characters.', {
            status: 400,
            retryable: false,
            fields: { highlights: `Each highlight must be 1 to ${ORG_HIGHLIGHT_MAX_LENGTH} characters.` }
          })
        }
      }

      return once(idempotencyKey, (): ProfileEditReceipt => {
        const appliedImmediately: string[] = []
        const pendingReview: string[] = []

        if (input.description !== undefined) {
          ownOrg.description = input.description
          pendingReview.push('/description')
        }
        if (input.highlights !== undefined) {
          ownOrg.highlights = [...input.highlights]
          pendingReview.push('/highlights')
        }
        if (input.image !== undefined) {
          if ('remove' in input.image) {
            // Immediate, and it clears the submitted version too — the fake's stand-in
            // for `data - 'image'` across every row. A removal that left the pending
            // version holding the picture would republish it on approval.
            ownOrg.image = null
            appliedImmediately.push('/image')
          } else {
            ownOrg.image = { url: uploaded.get(input.image.mediaId) ?? '', contentType: 'image/png' }
            pendingReview.push('/image')
          }
        }
        if (input.contact !== undefined) {
          ownOrg.contact = input.contact
          pendingReview.push('/contact')
        }

        if (pendingReview.length > 0) {
          ownOrg.approvalState = 'pending'
          ownOrg.version += 1
          ownOrg.pendingFields = pendingReview

          // The edit joins the staff profile queue, which is the whole point of
          // that queue existing: this org's account was approved long ago and
          // will never reappear in `listPendingAccounts`. Switching to the staff
          // role in dev now shows the change waiting, which is the loop this
          // work closes.
          //
          // `submittedAt` is set once, when the version opens, and left alone on
          // a later edit to the same pending version — the server's `created_at`
          // behaves the same way, and it is what stops a provider reordering the
          // queue by resubmitting.
          const existing = pendingProfiles.get('org_dev_provider')
          pendingProfiles.set('org_dev_provider', {
            orgId: 'org_dev_provider',
            orgName: 'Ridgeline Recovery Center',
            orgStatus: 'approved',
            version: ownOrg.version,
            approvalState: 'pending',
            liveVersion: existing?.liveVersion ?? ownOrg.version - 1,
            submittedAt: existing?.submittedAt ?? new Date().toISOString(),
            submitted: {
              description: ownOrg.description,
              highlights: [...ownOrg.highlights],
              image: ownOrg.image,
              contact: ownOrg.contact,
              pendingFields: pendingReview
            }
          })
        } else if (appliedImmediately.includes('/image')) {
          const pending = pendingProfiles.get('org_dev_provider')
          if (pending) pending.submitted = { ...pending.submitted, image: null }
        }
        ownOrg.updatedAt = new Date().toISOString()

        return {
          approvalState: ownOrg.approvalState,
          version: ownOrg.version,
          appliedImmediately,
          pendingReview
        }
      })
    },

    listPendingAccounts: async (query: AccountQueueQuery) => {
      await gate()
      if (isEmptyScenario()) return paginate<PendingAccount>([], query)
      const wanted = query.status ?? 'pending'
      return paginate(queue.filter(row => row.status === wanted), query)
    },

    listPendingProfiles: async (query: ProfileQueueQuery) => {
      await gate()
      if (isEmptyScenario()) return paginate<PendingProfile>([], query)
      const wanted = query.state ?? 'pending'
      const rows: PendingProfile[] = [...pendingProfiles.values()]
        .filter(entry => entry.approvalState === wanted)
        // Oldest first, on when the version was opened. The server sorts on
        // `created_at` for the same reason and explicitly not on the
        // last-updated stamp, which a bed-count change would move.
        .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt))
        .map(entry => ({
          orgId: entry.orgId,
          orgName: entry.orgName,
          orgStatus: entry.orgStatus,
          version: entry.version,
          approvalState: entry.approvalState,
          liveVersion: entry.liveVersion,
          submittedAt: entry.submittedAt
        }))
      return paginate(rows, query)
    },

    searchDirectory: async (query: DirectoryQuery): Promise<DirectoryResults> => {
      await gate()

      const location = query.location ?? {}
      const network = query.insuranceNetwork ?? 'any'
      const hasOrigin = location.originPostalCode !== undefined

      if (query.sort === 'distance' && !hasOrigin) {
        throw new ApiError('Sorting by distance needs an origin postal code.', {
          status: 400,
          retryable: false,
          fields: { sort: 'Add a postal code to measure from.' }
        })
      }

      const listings = isEmptyScenario() ? [] : LISTINGS

      const takes = (row: FakeListing, insurer: string): 'in_network' | 'out_of_network' | null =>
        row.inNetwork.includes(insurer)
          ? 'in_network'
          : network === 'any' && row.outOfNetwork.includes(insurer) ? 'out_of_network' : null

      const eligible = listings.filter(row => {
        if (query.orgId !== undefined && row.orgId !== query.orgId) return false
        if (query.programTypes?.length && !query.programTypes.includes(row.programTypeId)) return false
        if (location.region !== undefined && row.region !== location.region) return false
        if (location.city !== undefined && row.city?.toLowerCase() !== location.city.toLowerCase()) return false
        if (query.insurance?.length && !query.insurance.some(id => takes(row, id) !== null)) return false
        // All-of, both of them: a program serving one of a client's two
        // populations is not a fit, and one treating one of two diagnoses is not
        // a placement.
        if (query.populations?.length && !query.populations.every(id => row.populations.includes(id))) return false
        if (query.diagnoses?.length && !query.diagnoses.every(id => row.diagnoses.includes(id))) return false
        // The only axis phrased as an exclusion, and a hard drop: the referral
        // would be refused.
        if (query.admissionCriteria?.length && query.admissionCriteria.some(id => row.excludes.includes(id))) return false
        // All-of again. A client who needs autism-competent care and trauma care
        // is not served by a program offering one of the two.
        if (query.specialties?.length && !query.specialties.every(id => row.specialties.includes(id))) return false
        if (query.attendance?.length &&
          !(row.attendance ?? []).some(label => query.attendance!.includes(outpatientOption('iop_attendance', label) ?? ''))) {
          return false
        }
        // One level has to satisfy every schedule condition at once, as on the
        // server: an evening IOP and a short PHP are not a short evening program.
        const scheduled = query.levelsOfCare?.length || query.sessionTimes?.length || query.maxLengthWeeks !== undefined
        if (scheduled && !(row.dayTreatment ?? []).some(level =>
          (!query.levelsOfCare?.length || query.levelsOfCare.includes(outpatientOption('levels_of_care', level.level) ?? '')) &&
          (!query.sessionTimes?.length || level.tracks.some(track =>
            query.sessionTimes!.includes(track.start >= EVENING_FROM ? 'evening' : 'daytime'))) &&
          (query.maxLengthWeeks === undefined || level.weeks <= query.maxLengthWeeks))) {
          return false
        }
        return true
      })

      const radius = location.radiusMiles
      const excludedWithoutLocation = radius === undefined
        ? null
        : eligible.filter(row => row.distanceMiles === null).length

      const placed = radius === undefined
        ? eligible
        : eligible.filter(row => row.distanceMiles !== null && row.distanceMiles <= radius)

      const asked = query.ageMin !== undefined || query.ageMax !== undefined

      // Counted over what the radius left rather than over everything eligible,
      // the way the server does it: "matches your filters but published no ages"
      // is a false claim about a program the radius already put out of reach.
      const excludedWithoutAges = asked
        ? placed.filter(row => row.ageMin === null && row.ageMax === null).length
        : null

      // Overlap, not containment. A null on one end of the program's range is an
      // open bound and compares; a null on both is a silence, and the first test
      // drops it rather than letting the open-bound rule match everything.
      const matched = !asked
        ? placed
        : placed.filter(row =>
          (row.ageMin !== null || row.ageMax !== null) &&
          (query.ageMax === undefined || row.ageMin === null || row.ageMin <= query.ageMax) &&
          (query.ageMin === undefined || row.ageMax === null || row.ageMax >= query.ageMin))

      const sort = query.sort ?? (hasOrigin ? 'distance' : 'name')

      const ordered = [...matched].sort((a, b) => {
        if (sort === 'distance') {
          // Nulls last, like every other "we do not know" in this system.
          const left = a.distanceMiles ?? Number.POSITIVE_INFINITY
          const right = b.distanceMiles ?? Number.POSITIVE_INFINITY
          if (left !== right) return left - right
        }
        return a.name.localeCompare(b.name)
      })

      const page = paginate(
        ordered.map((row): DirectoryResult => ({
          programId: row.programId,
          name: row.name,
          programType: row.programType,
          orgId: row.orgId,
          orgName: row.orgName,
          city: row.city,
          region: row.region,
          distanceMiles: hasOrigin ? row.distanceMiles : null,
          distanceBasis: hasOrigin ? row.distanceBasis : null,
          insuranceNetwork: query.insurance?.length
            ? query.insurance.map(id => takes(row, id)).find(n => n !== null) ?? null
            : null,
          ageMin: row.ageMin,
          ageMax: row.ageMax,
          admissionsPhone: '+1 801 555 0100',
          // One company with a logo and the rest without, so both the picture
          // and the monogram fallback are on the page in the default scenario.
          orgImageUrl: row.orgId === 'org_liberty' ? FAKE_LOGO : null,
          houseCount: 1,
          specialties: row.specialties
            .map(id => TAXONOMY.find(term => term.id === id)?.label ?? id)
            .sort((a, b) => a.localeCompare(b))
        })),
        query)

      return { ...page, excludedWithoutLocation, excludedWithoutAges }
    },

    listPendingPrograms: async (query: ProgramQueueQuery) => {
      await gate()
      if (isEmptyScenario()) return paginate<PendingProgram>([], query)
      const wanted = query.state ?? 'pending'
      const rows = [...pendingPrograms.values()]
        .filter(entry => entry.approvalState === wanted)
        // Oldest first, on when the version was opened. The server sorts on
        // `created_at` for the same reason and explicitly not on the
        // last-updated stamp, which a bed-count change would move.
        .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt))
      return paginate(rows, query)
    },

    decideProgram: async (programId: string, input: ProgramDecisionInput, idempotencyKey: string): Promise<ProgramDecisionResult> => {
      await gate()
      const row = pendingPrograms.get(programId)
      if (row === undefined) throw new ApiError('That program does not exist.', { status: 404, retryable: false })

      return once(idempotencyKey, (): ProgramDecisionResult => {
        if (row.approvalState !== 'pending') {
          // Two reviewers, one queue. The server decides this in the UPDATE
          // rather than in the handler; a fake can only state the outcome.
          throw new ApiError(`Version ${row.version} is ${row.approvalState}, not pending. The first decision stands.`, {
            status: 409,
            retryable: false
          })
        }

        const approvalState: ApprovalState =
          input.decision === 'approve'
            ? 'approved'
            : input.decision === 'reject' ? 'rejected' : 'info_requested'

        pendingPrograms.set(programId, {
          ...row,
          approvalState,
          liveVersion: approvalState === 'approved' ? row.version : row.liveVersion
        })

        const own = programs.find(p => p.id === programId)
        if (own !== undefined) own.approvalState = approvalState

        return {
          programId,
          version: row.version,
          approvalState,
          decidedAt: new Date().toISOString()
        }
      })
    },

    decideAccount: async (orgId: string, input: AccountDecisionInput, idempotencyKey: string): Promise<AccountDecisionResult> => {
      await gate()
      const row = queue.find(r => r.orgId === orgId)
      if (!row) throw new ApiError('That organization does not exist.', { status: 404, retryable: false })

      return once(idempotencyKey, (): AccountDecisionResult => {
        if (row.status !== 'pending') {
          throw new ApiError('This account was already decided by another staff member.', {
            status: 409,
            retryable: false
          })
        }
        row.status = input.decision === 'approve' ? 'approved' : 'rejected'
        return { orgId, status: row.status, decidedAt: new Date().toISOString() }
      })
    },

    decideOrgProfile: async (orgId: string, input: ProfileDecisionInput, idempotencyKey: string): Promise<ProfileDecisionResult> => {
      await gate()
      const pending = pendingProfiles.get(orgId)
      if (!pending) {
        throw new ApiError('That organization has no profile to decide.', { status: 404, retryable: false })
      }

      return once(idempotencyKey, (): ProfileDecisionResult => {
        if (pending.approvalState !== 'pending') {
          throw new ApiError(`Version ${pending.version} is already ${pending.approvalState}.`, {
            status: 409,
            retryable: false
          })
        }
        pending.approvalState =
          input.decision === 'approve' ? 'approved' : input.decision === 'reject' ? 'rejected' : 'info_requested'

        // Approving is what puts the version in the directory, so it is also
        // what makes it the live one. Reject and request-information promote
        // nothing: the provider's edit stays on the version and the directory
        // keeps showing whatever was approved before it.
        if (pending.approvalState === 'approved') {
          pending.liveVersion = pending.version
        }

        // The signed-in provider's own screens read `ownOrg`, so a decision has
        // to reach it or ORG-05's banner would keep saying "pending" after
        // staff decided. Only for that org: the others have no owner session in
        // the fake.
        if (orgId === 'org_dev_provider') {
          ownOrg.approvalState = pending.approvalState
          ownOrg.pendingFields = pending.approvalState === 'approved' ? [] : ownOrg.pendingFields
        }

        return {
          orgId,
          version: pending.version,
          approvalState: pending.approvalState,
          decidedAt: new Date().toISOString()
        }
      })
    }
  }
}
